import dayjs from 'dayjs';
import { Outgoing } from './outgoing';
import { devLocalStorage } from '../../application/dev_local_storage';
import { BackendLogLevel } from '@src/api/models/BackEndLogEnum';

export namespace Logger {
	interface State {
		level: number;
		bitWise: number;
		storeValues: boolean;
	}

	// prettier-ignore
	export enum Level {
		error   = 1 << 0,
		warn    = 1 << 1,
		info    = 1 << 2,
		debug   = 1 << 3,
		verbose = 1 << 4,
		none    = 0 << 0,
	}

	/**
	 * @description
	 * Logger's internal state.
	 */
	export const state: State = initializeConfig();

	/**
	 * @description
	 * Array with all printed logs.
	 */
	export const storedLogs: string[] = [];

	/**
	 * @description
	 * Updates current binary representation. The representations are:
	 * - `1 = error` -> shows only errors
	 * - `2 = warn` -> shows warnings and all of above.
	 * - `4 = info` -> shows info and all of above.
	 * - `8 = debug` -> shows debug and all of above.
	 * - `16 = verbose` -> shows all.
	 * - `0 = none` -> no logs are printed.
	 *
	 * @param level Binary representation.
	 */
	export function setLogLevel(level: Level): void {
		const bitWise = getBitwise(level);

		if (bitWise !== 0 || level === 0) {
			state.level = level;
			state.bitWise = bitWise;
		}
	}

	/**
	 * @description
	 * Prints a log to the console, same log could also be sent to SEQ.
	 *
	 * @param level Visible at this flag's level.
	 * @param message Printed text.
	 * @param send Will send to SEQ if passed.
	 */
	export function assert(level: Level, message: string, send: Outgoing.SendObjectData | null = null): void {
		const timeStamp = new Date().toISOString();
		const str = `[${enumToString(level)} - ${dayjs(timeStamp).format('HH:mm:ss:SSS')}] ${message}`;

		if (storedLogs.length >= 10000) {
			print('output exceeds max entries', Level.warn);
		} else {
			if (state.storeValues) {
				storedLogs.push(str);
			}

			if ((level & state.bitWise) === level) {
				print(str, level);
			}
		}

		if (send !== null) {
			function clientEnumToBackendEnum(): BackendLogLevel {
				if (level === Level.error) {
					return BackendLogLevel.Error;
				}

				if (level === Level.debug) {
					return BackendLogLevel.Debug;
				}

				return BackendLogLevel.Info;
			}

			Outgoing.addEntry({
				...send,
				timeStamp,
				text: message,
				logLevel: clientEnumToBackendEnum(),
			});
		}
	}

	/**
	 * @description
	 * Print a styled message to the console.
	 *
	 * @param str Printed string
	 * @param level Uses different console methods depending on level.
	 */
	export function print(str: string, level?: Level): void {
		const stringModified = `%c${str}`;
		const css = `
			color: #bfbfbf;
			font-size: 12px;
			margin: 2px 0;
		`;

		if (level === Level.error) {
			console.error(stringModified, css);

			return;
		}

		if (level === Level.warn) {
			console.warn(stringModified, css);

			return;
		}

		if (level === Level.info) {
			console.info(stringModified, css);

			return;
		}

		console.log(stringModified, css);
	}

	function enumToString(level: Level): string {
		// Trailing spaces to aline text in console
		if ((level & Level.error) === Level.error) return 'error  ';
		if ((level & Level.warn) === Level.warn) return 'warn   ';
		if ((level & Level.debug) === Level.debug) return 'debug  ';
		if ((level & Level.info) === Level.info) return 'info   ';
		if ((level & Level.verbose) === Level.verbose) return 'verbose';
		if ((level & Level.none) === Level.none) return 'none   ';

		return '';
	}

	function getBitwise(level: Level): number {
		let bitWise;

		switch (level) {
			case Level.error:
				bitWise = Level.error;
				break;
			case Level.warn:
				bitWise = Level.warn | Level.error;
				break;
			case Level.info:
				bitWise = Level.info | Level.warn | Level.error;
				break;
			case Level.debug:
				bitWise = Level.debug | Level.info | Level.warn | Level.error;
				break;
			case Level.verbose:
				bitWise = Level.verbose | Level.debug | Level.info | Level.warn | Level.error;
				break;
			default:
				bitWise = 0;
		}

		return bitWise;
	}

	function initializeConfig(): State {
		const debugConfig = devLocalStorage.get<any>('config');

		if (!debugConfig) {
			const level = process.env.NODE_ENV === 'development' ? Level.verbose : Level.none;

			return {
				level,
				bitWise: getBitwise(level),
				storeValues: true,
			};
		}

		return {
			level: debugConfig.log.level,
			bitWise: getBitwise(debugConfig.log.level),
			storeValues: true,
		};
	}
}
